// To-scale roadmap: the PA window as a quiet bar under the forecast bar (coloured by
// health), milestone diamonds, a today line, month or quarter zoom. Admins drag a
// forecast end to reforecast, or focus it and use the arrow keys.
import { useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { trackerKey, type Task, type TrackerData, type TrackerPhase } from '@/lib/tracker';
import type { Program } from '@/lib/types';
import { cn, fmtDate, parseDate } from '@/lib/utils';
import { useToast } from '@/components/ui/toast';
import { HEALTH_VAR, HealthBadge } from '@/components/tracker/bits';

const DAY = 86_400_000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const LANE = 68;
const SUB = 30;
const HEADER = 48;
/** Bars and milestones are laid out for a 56px lane; this centres them in a taller one. */
const PAD = (LANE - 56) / 2;

const toIso = (ms: number) => new Date(ms).toISOString().slice(0, 10);
const addDays = (iso: string, n: number) => toIso(parseDate(iso).getTime() + n * DAY);

type End = 'forecastStart' | 'forecastEnd';

export function Roadmap({ program, data, canEdit }: { program: Program; data: TrackerData; canEdit: boolean }) {
  const [zoom, setZoom] = useState<'month' | 'quarter'>('month');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [drag, setDrag] = useState<{ phaseId: string; end: End; date: string } | null>(null);
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const start = parseDate(program.startDate).getTime();
  const lastPhaseEnd = Math.max(
    ...data.phases.map((p) => parseDate(p.forecastEnd).getTime()),
    parseDate(program.estimatedEndDate).getTime(),
  );
  const endDate = new Date(lastPhaseEnd);
  const end = Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth() + 1, 1);
  const ppd = zoom === 'month' ? 3.4 : 1.15;
  const width = Math.round(((end - start) / DAY) * ppd);
  const x = (iso: string, endOfDay = false) => ((parseDate(iso).getTime() - start) / DAY + (endOfDay ? 1 : 0)) * ppd;

  const save = useMutation({
    mutationFn: (v: { phaseId: string; patch: Partial<Record<End, string>> }) =>
      api.patch(`/api/programs/${program.id}/phases/${v.phaseId}`, v.patch),
    onSettled: () => qc.invalidateQueries({ queryKey: trackerKey(program.id) }),
    onError: (e) => toast.error(errorMessage(e)),
  });

  function commit(phase: TrackerPhase, endKey: End, date: string) {
    const prev = phase[endKey];
    if (date === prev) return;
    if (endKey === 'forecastEnd' ? date < phase.forecastStart : date > phase.forecastEnd) {
      toast.error('The forecast start must stay before the forecast end.');
      return;
    }
    qc.setQueryData<TrackerData>(trackerKey(program.id), (d) =>
      d ? { ...d, phases: d.phases.map((p) => (p.id === phase.id ? { ...p, [endKey]: date } : p)) } : d,
    );
    save.mutate(
      { phaseId: phase.id, patch: { [endKey]: date } },
      {
        onSuccess: () =>
          toast.ok(
            `Phase ${phase.number} forecast ${endKey === 'forecastEnd' ? 'end' : 'start'} moved to ${fmtDate(date)}.`,
            {
              label: 'Undo',
              onClick: () => save.mutate({ phaseId: phase.id, patch: { [endKey]: prev } }),
            },
          ),
      },
    );
  }

  // Month or quarter ticks across the whole span.
  const ticks = useMemo(() => {
    const out: Array<{ x: number; label: string; major: boolean }> = [];
    for (
      let d = new Date(start);
      d.getTime() < end;
      d = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1))
    ) {
      const m = d.getUTCMonth();
      const px = ((d.getTime() - start) / DAY) * ppd;
      if (zoom === 'month')
        out.push({
          x: px,
          label: m === 0 || !out.length ? `${MONTHS[m]} ${d.getUTCFullYear()}` : MONTHS[m]!,
          major: m === 0,
        });
      else if (m % 3 === 0 || !out.length)
        out.push({ x: px, label: `Q${Math.floor(m / 3) + 1} ${d.getUTCFullYear()}`, major: m === 0 });
    }
    return out;
  }, [start, end, ppd, zoom]);

  const rows = data.phases.flatMap((p) => {
    const lanes: Array<
      | { kind: 'phase'; phase: TrackerPhase }
      | { kind: 'ws'; phase: TrackerPhase; ws: { id: string; name: string }; tasks: Task[] }
    > = [{ kind: 'phase', phase: p }];
    if (open[p.id])
      for (const ws of p.workstreams)
        lanes.push({ kind: 'ws', phase: p, ws, tasks: data.tasks.filter((t) => t.workstreamId === ws.id) });
    return lanes;
  });
  const height = HEADER + rows.reduce((h, r) => h + (r.kind === 'phase' ? LANE : SUB), 0);
  const todayX = x(data.today);

  return (
    <div className="rounded-lg border border-border bg-surface shadow-xs">
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5">
        <p className="text-xs text-muted">
          {canEdit
            ? 'Drag the end of a forecast bar to reforecast, or focus it and use the arrow keys.'
            : 'Click a phase to open it.'}
        </p>
        <div className="flex rounded-md border border-border-strong p-0.5 text-xs" role="group" aria-label="Zoom">
          {(['month', 'quarter'] as const).map((z) => (
            <button
              key={z}
              onClick={() => setZoom(z)}
              aria-pressed={zoom === z}
              className={cn(
                'rounded px-2.5 py-1 capitalize',
                zoom === z ? 'bg-surface-3 font-medium' : 'text-muted hover:text-text',
              )}
            >
              {z}
            </button>
          ))}
        </div>
      </div>

      <div className="flex">
        {/* Sticky labels */}
        <div className="w-80 shrink-0 border-r border-border">
          <div
            style={{ height: HEADER }}
            className="flex items-end border-b border-border px-4 pb-1.5 text-[11px] font-medium uppercase tracking-wide text-subtle"
          >
            Phase
          </div>
          {rows.map((r) =>
            r.kind === 'phase' ? (
              <div
                key={r.phase.id}
                style={{ height: LANE }}
                className="flex items-center gap-2 border-b border-border px-3 last:border-b-0"
              >
                {r.phase.workstreams.length ? (
                  <button
                    className="text-subtle hover:text-text"
                    aria-label={open[r.phase.id] ? 'Hide workstreams' : 'Show workstreams'}
                    onClick={() => setOpen({ ...open, [r.phase.id]: !open[r.phase.id] })}
                  >
                    {open[r.phase.id] ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
                  </button>
                ) : (
                  <span className="w-3.5" />
                )}
                <button className="min-w-0 flex-1 text-left" onClick={() => nav(`phase/${r.phase.id}`)}>
                  <span className="flex items-center gap-1.5">
                    <span className="tabular text-xs font-semibold text-subtle">{r.phase.number}</span>
                    <span className="truncate text-[13px] font-medium hover:underline">{r.phase.name}</span>
                  </span>
                  <span className="mt-1.5 flex items-center gap-2 whitespace-nowrap pl-4 text-[11px] text-muted">
                    <HealthBadge health={r.phase.health} />
                    <span className="tabular truncate">
                      {r.phase.taskProgress.done}/{r.phase.taskProgress.total} tasks ·{' '}
                      {r.phase.deliverableProgress.done} of {r.phase.deliverableProgress.total} accepted
                    </span>
                  </span>
                </button>
              </div>
            ) : (
              <div
                key={r.ws.id}
                style={{ height: SUB }}
                className="flex items-center border-b border-border bg-surface-2/40 pl-12 pr-3 text-xs text-muted"
              >
                <span className="truncate">{r.ws.name}</span>
                <span className="ml-auto tabular text-subtle">{r.tasks.length}</span>
              </div>
            ),
          )}
        </div>

        {/* Scrollable to-scale chart */}
        <div className="min-w-0 flex-1 overflow-x-auto">
          <svg width={width} height={height} className="block select-none" role="img" aria-label="Program roadmap">
            {/* grid and tick labels */}
            {ticks.map((t, i) => (
              <g key={i}>
                <line
                  x1={t.x}
                  x2={t.x}
                  y1={HEADER - 14}
                  y2={height}
                  stroke="var(--border)"
                  strokeOpacity={t.major ? 1 : 0.6}
                />
                <text
                  x={t.x + 4}
                  y={HEADER - 18}
                  fontSize={11}
                  fill={t.major ? 'var(--text)' : 'var(--muted)'}
                  fontWeight={t.major ? 600 : 400}
                >
                  {t.label}
                </text>
              </g>
            ))}
            <line x1={0} x2={width} y1={HEADER} y2={HEADER} stroke="var(--border)" />

            {(() => {
              let y = HEADER;
              return rows.map((r) => {
                const top = y;
                if (r.kind === 'ws') {
                  y += SUB;
                  const dated = r.tasks.filter((t) => t.startDate || t.dueDate);
                  const from = dated.map((t) => t.startDate ?? t.dueDate!).sort()[0];
                  const to = dated
                    .map((t) => t.dueDate ?? t.startDate!)
                    .sort()
                    .at(-1);
                  return (
                    <g key={r.ws.id}>
                      <rect x={0} y={top} width={width} height={SUB} fill="var(--surface-2)" opacity={0.35} />
                      {from && to && (
                        <rect
                          x={x(from)}
                          y={top + (SUB - 8) / 2}
                          width={Math.max(4, x(to, true) - x(from))}
                          height={8}
                          rx={2}
                          fill="var(--border-strong)"
                        >
                          <title>{`${r.ws.name}: tasks from ${fmtDate(from)} to ${fmtDate(to)}`}</title>
                        </rect>
                      )}
                      <line x1={0} x2={width} y1={top + SUB} y2={top + SUB} stroke="var(--border)" />
                    </g>
                  );
                }
                y += LANE;
                const p = r.phase;
                const fs = drag?.phaseId === p.id && drag.end === 'forecastStart' ? drag.date : p.forecastStart;
                const fe = drag?.phaseId === p.id && drag.end === 'forecastEnd' ? drag.date : p.forecastEnd;
                const color = HEALTH_VAR[p.health.effective];
                return (
                  <g key={p.id}>
                    {/* PA window */}
                    <rect
                      x={x(p.plannedStart)}
                      y={top + PAD + 34}
                      width={x(p.plannedEnd, true) - x(p.plannedStart)}
                      height={6}
                      rx={3}
                      fill="var(--surface-3)"
                      stroke="var(--border-strong)"
                      strokeWidth={0.75}
                    >
                      <title>{`PA window: ${fmtDate(p.plannedStart)} to ${fmtDate(p.plannedEnd)}`}</title>
                    </rect>
                    {/* forecast */}
                    <rect
                      x={x(fs)}
                      y={top + PAD + 13}
                      width={Math.max(4, x(fe, true) - x(fs))}
                      height={16}
                      rx={4}
                      fill={color}
                      fillOpacity={0.82}
                      className="cursor-pointer"
                      onClick={() => nav(`phase/${p.id}`)}
                    >
                      <title>{`Phase ${p.number} forecast: ${fmtDate(fs)} to ${fmtDate(fe)}`}</title>
                    </rect>
                    {canEdit &&
                      (['forecastStart', 'forecastEnd'] as End[]).map((endKey) => (
                        <EndHandle
                          key={endKey}
                          cx={endKey === 'forecastStart' ? x(fs) : x(fe, true)}
                          top={top}
                          label={`Phase ${p.number} forecast ${endKey === 'forecastStart' ? 'start' : 'end'}`}
                          date={endKey === 'forecastStart' ? fs : fe}
                          ppd={ppd}
                          onPreview={(date) => setDrag(date ? { phaseId: p.id, end: endKey, date } : null)}
                          onCommit={(date) => {
                            setDrag(null);
                            commit(p, endKey, date);
                          }}
                          base={p[endKey]}
                        />
                      ))}
                    {drag?.phaseId === p.id && (
                      <text
                        x={drag.end === 'forecastStart' ? x(fs) : x(fe, true)}
                        y={top + PAD + 10}
                        fontSize={11}
                        textAnchor="middle"
                        fill="var(--text)"
                        fontWeight={600}
                      >
                        {fmtDate(drag.date)}
                      </text>
                    )}
                    {p.milestones.map((m) => {
                      const mx = x(m.forecastDate, true);
                      const cy = top + PAD + 21;
                      const late = m.status === 'PLANNED' && m.forecastDate < data.today;
                      return (
                        <g key={m.id}>
                          <polygon
                            points={`${mx},${cy - 7} ${mx + 7},${cy} ${mx},${cy + 7} ${mx - 7},${cy}`}
                            fill={m.status === 'ACHIEVED' ? 'var(--ok)' : late ? 'var(--bad)' : 'var(--surface)'}
                            stroke={late ? 'var(--bad)' : 'var(--text)'}
                            strokeWidth={1.5}
                          >
                            <title>
                              {`${m.name}\nForecast ${fmtDate(m.forecastDate)} (planned ${fmtDate(m.plannedDate)})\n${
                                m.status === 'ACHIEVED'
                                  ? `Achieved ${fmtDate(m.achievedDate)}`
                                  : late
                                    ? 'Past due'
                                    : m.status === 'CANCELLED'
                                      ? 'Cancelled'
                                      : 'Planned'
                              }`}
                            </title>
                          </polygon>
                        </g>
                      );
                    })}
                    <line x1={0} x2={width} y1={top + LANE} y2={top + LANE} stroke="var(--border)" />
                  </g>
                );
              });
            })()}

            {todayX >= 0 && todayX <= width && (
              <g>
                <line x1={todayX} x2={todayX} y1={HEADER - 6} y2={height} stroke="var(--brand)" strokeWidth={1.5} />
                <text x={todayX + 4} y={HEADER - 4} fontSize={10} fill="var(--brand-text)" fontWeight={600}>
                  Today
                </text>
              </g>
            )}
          </svg>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-4 border-t border-border px-4 py-2 text-[11px] text-muted">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-1.5 w-6 rounded-full bg-surface-3 ring-1 ring-border-strong" /> PA window
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-6 rounded" style={{ background: 'var(--ok)' }} /> Forecast, coloured by
          health
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="12" height="12" aria-hidden>
            <polygon points="6,0 12,6 6,12 0,6" fill="var(--surface)" stroke="var(--text)" strokeWidth="1.5" />
          </svg>
          Milestone
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-px bg-brand" /> Today
        </span>
      </div>
    </div>
  );
}

/** A draggable, keyboard-operable forecast end. */
function EndHandle({
  cx,
  top,
  label,
  date,
  base,
  ppd,
  onPreview,
  onCommit,
}: {
  cx: number;
  top: number;
  label: string;
  date: string;
  base: string;
  ppd: number;
  onPreview: (date: string | null) => void;
  onCommit: (date: string) => void;
}) {
  const startX = useRef<number | null>(null);
  const current = useRef(base);

  const onPointerDown = (e: PointerEvent<SVGRectElement>) => {
    e.stopPropagation();
    (e.target as Element).setPointerCapture(e.pointerId);
    startX.current = e.clientX;
    current.current = base;
  };
  const onPointerMove = (e: PointerEvent<SVGRectElement>) => {
    if (startX.current === null) return;
    const days = Math.round((e.clientX - startX.current) / ppd);
    current.current = addDays(base, days);
    onPreview(current.current);
  };
  const onPointerUp = () => {
    if (startX.current === null) return;
    startX.current = null;
    onCommit(current.current);
  };
  const onKeyDown = (e: KeyboardEvent<SVGRectElement>) => {
    const step = e.shiftKey ? 7 : 1;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      onPreview(addDays(date, e.key === 'ArrowRight' ? step : -step));
    } else if (e.key === 'Enter') {
      onCommit(date);
    } else if (e.key === 'Escape') {
      onPreview(null);
    }
  };

  return (
    <rect
      x={cx - 5}
      y={top + PAD + 9}
      width={10}
      height={24}
      rx={3}
      fill="transparent"
      className="cursor-ew-resize outline-none focus:fill-[var(--ring)] focus:[fill-opacity:0.25] hover:fill-[var(--text)] hover:[fill-opacity:0.12]"
      tabIndex={0}
      role="slider"
      aria-label={`${label}, ${fmtDate(date)}. Arrow keys move a day, Shift moves a week, Enter saves, Escape cancels.`}
      aria-valuetext={fmtDate(date)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onKeyDown={onKeyDown}
      onBlur={() => date !== base && onPreview(null)}
    />
  );
}
